// src/components/sidebar/sidebar-elements.jsx
'use client'

import clsx from 'clsx'
import { LayoutGroup, motion } from 'framer-motion'
import React, { useId } from 'react'
import { TouchTarget } from './button'
import { Link } from './link'

/* =========================== Sidebar Container =========================== */
function Sidebar({ className, ...props }) {
  return (
    <nav
      {...props}
      className={clsx(
        className,
        'flex h-full min-h-0 flex-col bg-gray/75 backdrop-blur-xl dark:bg-zinc-900/75 border-zinc-950/5 dark:border-white/5'
      )}
    />
  )
}

/* ============================ Sidebar Header ============================= */
function SidebarHeader({ className, logo, actions, children, ...props }) {
  const kids = React.Children.toArray(children)
  const logoNode = logo ?? kids[0] ?? null
  const extraChildren = kids.length > 1 ? kids.slice(1) : []
  const actionsNode =
    actions ??
    (extraChildren.length > 0 ? <div className="flex items-center gap-2">{extraChildren}</div> : null)

  return (
    <div
      {...props}
      className={clsx(
        className,
        'flex items-center justify-between border-b border-zinc-950/5 px-4 pt-5 pb-3 dark:border-white/5'
      )}
    >
      <div className="min-w-0">{logoNode}</div>
      {actionsNode ? <div className="lg:hidden">{actionsNode}</div> : null}
    </div>
  )
}

/* ============================= Sidebar Body ============================== */
function SidebarBody({ className, ...props }) {
  return (
    <div
      {...props}
      className={clsx(className, 'flex flex-1 flex-col overflow-y-auto p-4 gap-1')}
    />
  )
}

/* ============================ Sidebar Footer ============================= */
function SidebarFooter({ className, ...props }) {
  return (
    <div
      {...props}
      className={clsx(
        className,
        'flex flex-col border-t border-stone-50/5 p-4 dark:border-white/5'
      )}
    />
  )
}

/* ============================ Sidebar Section ============================ */
function SidebarSection({ className, ...props }) {
  let id = useId()
  return (
    <LayoutGroup id={id}>
      <div {...props} className={clsx(className, 'flex flex-col gap-0.5')} />
    </LayoutGroup>
  )
}

/* ============================== Sidebar Item ============================= */
function SidebarItem({ current, className, children, onClick, ...props }) {
  const classes = clsx(
    // Base
    'flex w-full items-center gap-3 rounded-lg px-2 py-2.5 text-left text-base/6 font-medium text-white sm:py-2 sm:text-sm/5',

    // Leading icon/icon-only
    '*:data-[slot=icon]:size-6',
    '*:data-[slot=icon]:shrink-0',
    '*:data-[slot=icon]:data-[slot=icon]:size-5',

    // Trailing icon (e.g., chevron)
    '*:last:data-[slot=icon]:ml-auto',
    '*:last:data-[slot=icon]:size-5',
    'sm:*:last:data-[slot=icon]:size-4',

    // Avatar
    '*:data-[slot=avatar]:-m-0.5',
    '*:data-[slot=avatar]:size-7',
    '*:data-[slot=avatar]:[--ring-opacity:10%]',
    'sm:*:data-[slot=avatar]:size-11',

    // Hover
    'data-hover:bg-white/5',
    'data-hover:*:data-[slot=icon]:fill-white',

    // Active (use bright blue tint)
    'data-active:text-white',
    'data-active:bg-[#3483FE1A]', // ~10% blue
    'data-active:*:data-[slot=icon]:fill-white',

    // Current (stronger blue)
    'data-current:text-white',
    'data-current:bg-[#3483FE33]', // ~20% blue
    'data-current:*:data-[slot=icon]:fill-white',

    // Dark (kept, icons stay readable)
    'dark:text-white',
    'dark:*:data-[slot=icon]:fill-gray-400',
    'dark:data-hover:bg-white/5',
    'dark:data-hover:*:data-[slot=icon]:fill-white',
    'dark:data-active:bg-[#3483FE1A]',
    'dark:data-active:*:data-[slot=icon]:fill-white',
    'dark:data-current:bg-[#3483FE33]',
    'dark:data-current:*:data-[slot=icon]:fill-white'
  )

  const Tag = 'href' in props ? Link : 'button'
  const handleClick = (e) => onClick?.(e)

  return (
    <span className={clsx(className, 'relative')}>
      {current && (
        <motion.span
          layoutId="current-indicator"
          className="absolute inset-y-2 -left-4 w-0.5 rounded-full bg-[#3483FE]"
        />
      )}

      <Tag
        {...props}
        className={clsx(Tag === 'button' ? 'cursor-pointer' : '', classes)}
        data-current={current ? 'true' : undefined}
        type={Tag === 'button' ? 'button' : undefined}
        onClick={Tag === 'button' ? handleClick : undefined}
      >
        <TouchTarget>{children}</TouchTarget>
      </Tag>
    </span>
  )
}

/* ============================ Misc Primitives ============================ */
function SidebarLabel({ className, ...props }) {
  return <span {...props} className={clsx(className, 'truncate')} />
}

function SidebarSpacer({ className, ...props }) {
  return <div aria-hidden="true" {...props} className={clsx(className, 'mt-8 flex-1')} />
}

function SidebarHeading({ className, ...props }) {
  return (
    <h3
      {...props}
      className={clsx(
        className,
        'mb-1 px-2 text-xs/6 font-medium text-gray-400 dark:text-zinc-400 tracking-wide uppercase'
      )}
    />
  )
}

export {
  Sidebar,
  SidebarHeader,
  SidebarBody,
  SidebarFooter,
  SidebarSection,
  SidebarItem,
  SidebarLabel,
  SidebarSpacer,
  SidebarHeading
}
